import { all, fork, put, takeLatest } from "redux-saga/effects"
import { call } from "typed-redux-saga"

import { AvailablePlanStatus } from "@common-shared/config/coverage.config.ts"

import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"
import { UtilHelper } from "@front-shared/helpers/util.helper.ts"

import {
    IntakeDemoActions,
    intakeDemoActionCreators,
} from "../actions/intakeDemo.actions"
import { runtimeActionCreators } from "../actions/runtime.actions"
import { IntakeDemoFlow } from "../config/intakeDemo.config"
import { IntakeDemoService } from "../services/intakeDemo.service.ts"
import type {
    IGetCoverageInitialData,
    IGetCoveragePayerData,
} from "../types/data.types.ts"
import type { ISagaAction } from "../types/general.types.ts"

////////////////////////////////////////////////////////////////////////////////////////// INTAKE DEMO AUTH
function* intakeDemoStartAuthSaga() {
    yield takeLatest(
        IntakeDemoActions.INTAKE_DEMO_AUTH_START,
        intakeDemoStartAuthSagaHandler,
    )
}

function* intakeDemoStartAuthSagaHandler<
    T extends ISagaAction & {
        authToken: string
    },
>(action: T) {
    yield put(
        runtimeActionCreators.startLoading("intakeDemoStartAuthSagaHandler"),
    )

    try {
        const result = yield* call(
            IntakeDemoService.authorize,
            action.payload.authToken,
        )

        if (result) {
            yield put(
                intakeDemoActionCreators.authSuccess(action.payload.authToken),
            )

            void action.onSuccess?.(result)
        } else {
            action.onError?.(action.payload)

            ToastrHelper.error("Unfortunately auth failed.")

            yield put(intakeDemoActionCreators.authReset())
        }
    } catch (e) {
        console.error(e)
    } finally {
        yield put(
            runtimeActionCreators.stopLoading("intakeDemoStartAuthSagaHandler"),
        )
    }
}
////////////////////////////////////////////////////////////////////////////////////////// INTAKE DEMO AUTH

////////////////////////////////////////////////////////////////////////////////////////// INTAKE DEMO GET COVERAGE
function* intakeDemoGetCoverageSaga() {
    yield takeLatest(
        IntakeDemoActions.INTAKE_DEMO_GET_COVERAGE,
        intakeDemoGetCoverageSagaHandler,
    )
}

function* intakeDemoGetCoverageSagaHandler<
    T extends ISagaAction & {
        initialData: IGetCoverageInitialData
        payerData?: IGetCoveragePayerData
    },
>(action: T) {
    yield put(
        runtimeActionCreators.startLoading("intakeDemoGetCoverageSagaHandler"),
    )

    try {
        // Sleep for X seconds here so breathing spinner stays longer during this process
        yield call(UtilHelper.sleep, 3)

        const result = yield* call(
            IntakeDemoService.getCoverage,
            action.payload.initialData,
            action.payload.payerData,
        )

        if (result?.planStatus === AvailablePlanStatus.active) {
            yield put(intakeDemoActionCreators.setCoverage(result))

            void action.onSuccess?.(result)
        } else {
            action.onError?.(action.payload)

            yield put(
                intakeDemoActionCreators.setCurrentFlow(
                    action.payload.payerData
                        ? IntakeDemoFlow.failure
                        : IntakeDemoFlow.payerData,
                ),
            )
        }
    } catch (e) {
        console.error(e)
    } finally {
        yield put(
            runtimeActionCreators.stopLoading(
                "intakeDemoGetCoverageSagaHandler",
            ),
        )
    }
}
////////////////////////////////////////////////////////////////////////////////////////// INTAKE DEMO GET COVERAGE

export default function* rootSaga() {
    yield all([fork(intakeDemoStartAuthSaga), fork(intakeDemoGetCoverageSaga)])
}
